import { Injectable, signal, computed, effect, inject } from '@angular/core';
import { BehaviorSubject, Observable, Subject, map } from 'rxjs';
import { SignalingMessage } from '../models/webrtc.models';
import { DeviceService } from './device.service';
import { DebugLoggerService } from './debug-logger.service';
import { StorageService } from './storage.service';

export interface PeerInfo {
  deviceId: string;
  deviceName?: string;
  platform?: string;
  isCreator: boolean;
  connected?: boolean;
  lastSeen?: number;
  // Enhanced user identification
  userPhoneNumber?: string;
  userName?: string;
  userDisplayName?: string;
  abbreviatedName?: string;
  // Event data
  attendeeCount?: number;
  eventCount?: number;
  deviceCount?: number; // NEW: Device-specific count for audit purposes
  // NEW: Disconnection tracking
  disconnectedAt?: number;
  lastKnownAttendeeCount?: number;
  lastKnownDeviceCount?: number;
  // Background tracking
  isInBackground?: boolean;
  lastBackgroundPing?: number;
  backgroundPingCount?: number;
}

@Injectable({
  providedIn: 'root'
})
export class WebSocketSignalingService {
  private ws: WebSocket | null = null;
  private reconnectInterval = 5000;
  private maxReconnectAttempts = 10;
  private reconnectAttempts = 0;
  private serverUrl = 'wss://ws.aforo.bink.tools:8081';
  // Keepalive to prevent intermediaries from closing idle connections
  private keepAliveIntervalMs = 25000; // 25s to stay below common 30/60s idle timeouts
  private keepAliveTimer: any | null = null;
  private deviceId = '';
  private currentEventId: string | null = null;

  // New signal to track if a connection attempt has been initiated
  private readonly _connectionInitiatedSignal = signal<boolean>(false);
  private readonly _connectionStatusSignal = signal<boolean>(false);
  private readonly _clientIdSignal = signal<string | null>(null);
  private readonly _totalClientsSignal = signal<number>(0);
  private readonly _peersSignal = signal<string[]>([]);
  private readonly _connectedDevicesSignal = signal<Map<string, PeerInfo>>(new Map());
  private readonly _messagesSignal = signal<SignalingMessage | null>(null);

  // NEW: Public readonly signals
  public readonly connectionStatusSignal = this._connectionStatusSignal.asReadonly();
  public readonly clientIdSignal = this._clientIdSignal.asReadonly();
  public readonly totalClientsSignal = this._totalClientsSignal.asReadonly();
  public readonly peersSignal = this._peersSignal.asReadonly();
  public readonly connectedDevicesSignal = this._connectedDevicesSignal.asReadonly();
  public readonly currentMessageSignal = this._messagesSignal.asReadonly();

  // NEW: Computed signals for derived state
  public readonly isConnectedSignal = computed(() => this.connectionStatusSignal());
  public readonly connectionStateSignal = computed(() =>
    this.connectionStatusSignal() ? 'connected' : 'disconnected'
  );
  public readonly deviceCountSignal = computed(() => this.connectedDevicesSignal().size);
  public readonly peerCountSignal = computed(() => this.peersSignal().length);
  public readonly hasActivePeersSignal = computed(() => this.peerCountSignal() > 0);

  public readonly connectionInitiatedSignal = this._connectionInitiatedSignal.asReadonly();

  // BACKWARD COMPATIBILITY: Keep observables for gradual migration
  private _connectionStatus = new BehaviorSubject<boolean>(false);
  private _clientId = new BehaviorSubject<string | null>(null);
  private _totalClients = new BehaviorSubject<number>(0);
  private _messages = new Subject<SignalingMessage>();
  private _peers = new BehaviorSubject<string[]>([]);
  private _connectedDevices = new BehaviorSubject<Map<string, PeerInfo>>(new Map());

  public connectionStatus$ = this._connectionStatus.asObservable();
  public connectionState$ = this._connectionStatus.pipe(
    map(connected => connected ? 'connected' : 'disconnected')
  ); // String-based for compatibility
  public clientId$ = this._clientId.asObservable();
  public totalClients$ = this._totalClients.asObservable();
  public messages$ = this._messages.asObservable();
  public peers$ = this._peers.asObservable();
  public connectedDevices$ = this._connectedDevices.asObservable();

  constructor(private deviceService: DeviceService, private logger: DebugLoggerService, private storage: StorageService) {
    // Sync signals with legacy observables during transition
    effect(() => {
      this._connectionStatus.next(this._connectionStatusSignal());
    });

    effect(() => {
      this._clientId.next(this._clientIdSignal());
    });

    effect(() => {
      this._totalClients.next(this._totalClientsSignal());
    });

    effect(() => {
      this._peers.next(this._peersSignal());
    });

    effect(() => {
      this._connectedDevices.next(this._connectedDevicesSignal());
    });

    effect(() => {
      const message = this._messagesSignal();
      if (message) {
        this._messages.next(message);
      }
    });

    this.initializeDeviceInfo();
  }

  private async initializeDeviceInfo(): Promise<void> {
    try {
      const deviceInfo = await this.deviceService.getDeviceInfo();
      this.deviceId = deviceInfo.deviceId;
    } catch (error) {
      this.logger.warn('WebSocketSignaling', 'Failed to get device info, using fallback:', error);
      // Fallback device identification
      this.deviceId = this.generateDeviceId();
    }
  }

  connect(serverUrl?: string): Promise<boolean> {
    const url = serverUrl || this.serverUrl;

    // Set connection initiated signal
    this._connectionInitiatedSignal.set(true);

    return new Promise((resolve) => {
      if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
        this.logger.log('WebSocketSignaling', `WebSocket already in state: ${this.ws.readyState === WebSocket.OPEN ? 'OPEN' : 'CONNECTING'}`);
        // If already connecting, wait for that connection to resolve
        if (this.ws.readyState === WebSocket.CONNECTING) {
          const subscription = this._connectionStatus.subscribe(connected => {
            if (connected || this.ws?.readyState === WebSocket.CLOSED) { // Also resolve if it closed
              subscription.unsubscribe();
              resolve(connected);
            }
          });
        } else {
          resolve(true);
        }
        return;
      }

      try {
        this.logger.log('WebSocketSignaling', `Attempting WebSocket connection to: ${url}`);
        this.ws = new WebSocket(url);
        this.setupWebSocketHandlers();

        // Extended timeout for older Android devices
        const timeout = setTimeout(() => {
          this.logger.warn('WebSocketSignaling', 'WebSocket connection timeout - may be due to network restrictions on older devices');
          if (this.ws && this.ws.readyState === WebSocket.CONNECTING) {
            this.ws.close();
          }
          resolve(false);
        }, 10000); // Increased timeout to 10 seconds

        const subscription = this._connectionStatus.subscribe(connected => {
          if (connected) {
            clearTimeout(timeout);
            subscription.unsubscribe();
            resolve(true);
          }
        });
      } catch (error) {
        this.logger.error('WebSocketSignaling', 'Failed to connect to signaling server', error);
        this.logger.log('WebSocketSignaling', 'This may be due to network restrictions on older Android devices');
        this.scheduleReconnect(url);
        resolve(false);
      }
    });
  }

  disconnect(): void {
    this.logger.log('WebSocketSignaling', 'Attempting to disconnect WebSocket');
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    // Clear keepalive timer
    if (this.keepAliveTimer) {
      clearInterval(this.keepAliveTimer);
      this.keepAliveTimer = null;
    }
    // Update both signals and legacy observables
    this._connectionStatusSignal.set(false);
    this._clientIdSignal.set(null);
    this._totalClientsSignal.set(0);
    this._peersSignal.set([]);
    this._connectedDevicesSignal.set(new Map());
    this._connectionInitiatedSignal.set(false); // Set to false on disconnect

    this._connectionStatus.next(false);
    this._clientId.next(null);
    this._totalClients.next(0);
    this._peers.next([]);
    this.reconnectAttempts = 0;
    this.logger.log('WebSocketSignaling', 'WebSocket disconnected state set');
  }

  sendMessage(message: SignalingMessage): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    } else {
      this.logger.error('WebSocketSignaling', 'WebSocket is not connected');
    }
  }

  sendOffer(targetId: string, offer: RTCSessionDescriptionInit): void {
    this.sendMessage({
      type: 'offer',
      targetId,
      data: offer
    });
  }

  sendAnswer(targetId: string, answer: RTCSessionDescriptionInit): void {
    this.sendMessage({
      type: 'answer',
      targetId,
      data: answer
    });
  }

  sendIceCandidate(targetId: string, candidate: RTCIceCandidateInit): void {
    this.sendMessage({
      type: 'ice-candidate',
      targetId,
      data: candidate
    });
  }

  async sendDeviceIdentification(): Promise<void> {
    try {
      const deviceInfo = await this.deviceService.getDeviceInfo();
      this.sendMessage({
        type: 'device-identification',
        deviceId: deviceInfo.deviceId,
        deviceName: deviceInfo.deviceName,
        platform: deviceInfo.platform,
        userDisplayName: deviceInfo.userDisplayName
      });
      this.logger.log('WebSocketSignaling', 'Device identification sent to WebRTC mesh server');
    } catch (error) {
      this.logger.error('WebSocketSignaling', 'Failed to send device identification:', error);
    }
  }

  ping(): void {
    this.sendMessage({ type: 'ping' });
  }

  sendAttendanceUpdate(payload: {
    eventId: string;
    deviceId: string;
    deviceCount: number;
    totalCount: number;
    deviceName?: string;
    platform?: string;
    userDisplayName?: string;
  }): void {
    this.sendMessage({
      type: 'attendance-update',
      eventId: payload.eventId,
      deviceId: payload.deviceId,
      deviceCount: payload.deviceCount,
      totalCount: payload.totalCount,
      attendeeCount: payload.deviceCount,
      deviceName: payload.deviceName,
      platform: payload.platform,
      userDisplayName: payload.userDisplayName,
      timestamp: Date.now()
    });
  }

  /**
   * Request the latest peer list from the server
   */
  refreshPeerList(): void {
    if (!this.currentEventId || !this.isConnected) {
      return;
    }
    
    this.logger.log('WebSocketSignaling', 'Requesting peer list refresh for event:', this.currentEventId);
    
    // The server will respond with 'event-peers' message
    this.sendMessage({
      type: 'request-peer-list',
      eventId: this.currentEventId
    });
  }

  // Event-based connection methods
  async joinEventRoom(eventId: string, eventPassword: string): Promise<boolean> {
    if (!this.isConnected) {
      this.logger.error('WebSocketSignaling', 'Cannot join event room: not connected to signaling server');
      return false;
    }

    if (this.currentEventId && this.currentEventId !== eventId) {
      this._connectedDevicesSignal.set(new Map());
      this._connectedDevices.next(new Map());
      this._peers.next([]);
    }

    this.currentEventId = eventId;

    this.sendMessage({
      type: 'join-event',
      eventId: eventId,
      password: eventPassword // Pass the password to the server
    });
    void this.sendDeviceIdentification();

    // Load and merge disconnected devices for this event
    await this.mergeDisconnectedDevices(eventId);

    // Clean up old disconnected devices (older than 24 hours)
    await this.cleanupOldDisconnectedDevices(eventId, 24);

    this.logger.log('WebSocketSignaling', `Joined event room: ${eventId}`);
    return true;
  }

  getCurrentEventId(): string | null {
    return this.currentEventId;
  }

  private isCurrentEvent(eventId: string | number | null | undefined): boolean {
    if (eventId === undefined || eventId === null || !this.currentEventId) {
      return false;
    }
    return String(eventId) === String(this.currentEventId);
  }

  // Compatibility methods for existing components
  async createEvent(eventId: string, eventName: string, password: string, eventType: string): Promise<boolean> {
    this.logger.warn('WebSocketSignaling', 'createEvent: Connecting to signaling server and joining event room');
    const connected = await this.connect();
    if (connected && eventId) {
      await this.joinEventRoom(eventId, password);
    }
    return connected;
  }

  async joinEvent(eventId: string, password: string): Promise<{ success: boolean; eventName?: string }> {
    // Connect to the server and join the specific event room
    const success = await this.connect();

    if (success && eventId) {
      await this.joinEventRoom(eventId, password);
    }

    // DON'T save event details to localStorage when joining
    // The event should only be saved by the EventService when properly received
    // from the sharing device with the correct ID and name

    this.logger.log('WebSocketSignaling', `Join attempt for event ${eventId}: ${success ? 'success' : 'failed'}`);

    return { success, eventName: eventId };
  }

  getServerUrl(): string {
    return this.serverUrl;
  }

  setServerUrl(url: string): void {
    this.serverUrl = url;
  }

  getDeviceId(): string {
    return this.deviceId;
  }

  async clearDeviceId(): Promise<void> {
    this.deviceId = this.generateDeviceId();
  }

  getConnectedDevices(): Map<string, PeerInfo> {
    return this._connectedDevices.value;
  }

  updatePeerDeviceInfo(peerId: string, info: Partial<PeerInfo>): void {
    this.logger.log('WebSocketSignaling', `updatePeerDeviceInfo called for peer ${peerId} with info:`, info);
    const devices = this._connectedDevices.value;
    const existingPeer = devices.get(peerId);

    if (existingPeer) {
      const updatedPeer: PeerInfo = {
        ...existingPeer,
        deviceName: info.deviceName || existingPeer.deviceName,
        platform: info.platform || existingPeer.platform,
        deviceId: info.deviceId || existingPeer.deviceId,
        lastSeen: Date.now(),
        // Enhanced user identification
        userPhoneNumber: info.userPhoneNumber || existingPeer.userPhoneNumber,
        userName: info.userName || existingPeer.userName,
        userDisplayName: info.userDisplayName || existingPeer.userDisplayName,
        abbreviatedName: info.abbreviatedName || existingPeer.abbreviatedName,
        // Event data
        attendeeCount: info.attendeeCount ?? existingPeer.attendeeCount,
        eventCount: info.eventCount ?? existingPeer.eventCount,
        deviceCount: info.deviceCount ?? existingPeer.deviceCount, // NEW: Include device count for audit purposes
        // Background tracking
        isInBackground: info.isInBackground ?? existingPeer.isInBackground,
        lastBackgroundPing: info.lastBackgroundPing ?? existingPeer.lastBackgroundPing,
        backgroundPingCount: info.backgroundPingCount ?? existingPeer.backgroundPingCount,
        // Creator status
        isCreator: info.isCreator ?? existingPeer.isCreator
      };

      const newDevices = new Map(devices);
      newDevices.set(peerId, updatedPeer);
      this._connectedDevicesSignal.set(newDevices);
      this._connectedDevices.next(newDevices);

      this.logger.log('WebSocketSignaling', `Updated enhanced peer device info for ${peerId}:`, {
        deviceName: updatedPeer.deviceName,
        platform: updatedPeer.platform,
        userDisplayName: updatedPeer.userDisplayName,
        abbreviatedName: updatedPeer.abbreviatedName,
        attendeeCount: updatedPeer.attendeeCount,
        deviceCount: updatedPeer.deviceCount,
        isCreator: updatedPeer.isCreator
      });
    } else {
      // Create new peer entry if it doesn't exist
      const newPeer: PeerInfo = {
        deviceId: info.deviceId || peerId,
        deviceName: info.deviceName,
        platform: info.platform,
        isCreator: info.isCreator || false,
        connected: true,
        lastSeen: Date.now(),
        // Enhanced user identification
        userPhoneNumber: info.userPhoneNumber,
        userName: info.userName,
        userDisplayName: info.userDisplayName,
        abbreviatedName: info.abbreviatedName,
        // Event data
        attendeeCount: info.attendeeCount,
        eventCount: info.eventCount,
        deviceCount: info.deviceCount // NEW: Include device count for audit purposes
      };

      const newDevices = new Map(devices);
      newDevices.set(peerId, newPeer);
      this._connectedDevicesSignal.set(newDevices);
      this._connectedDevices.next(newDevices);

      this.logger.log('WebSocketSignaling', `Created new peer device info for ${peerId}:`, {
        deviceName: newPeer.deviceName,
        platform: newPeer.platform,
        userDisplayName: newPeer.userDisplayName,
        abbreviatedName: newPeer.abbreviatedName,
        attendeeCount: newPeer.attendeeCount,
        deviceCount: newPeer.deviceCount,
        isCreator: newPeer.isCreator
      });
    }
  }

  private upsertPeerAttendance(message: {
    clientId?: string;
    deviceId?: string;
    deviceName?: string;
    platform?: string;
    userDisplayName?: string;
    deviceCount?: number;
    attendeeCount?: number;
  }): void {
    const senderDeviceId = message.deviceId;
    if (!senderDeviceId || senderDeviceId === this.deviceId) {
      return;
    }

    const devices = new Map(this._connectedDevices.value);
    let key = message.clientId && devices.has(message.clientId)
      ? message.clientId
      : null;
    if (!key) {
      for (const [existingKey, peer] of devices.entries()) {
        if (peer.deviceId === senderDeviceId) {
          key = existingKey;
          break;
        }
      }
    }
    if (!key) {
      key = message.clientId || senderDeviceId;
    }

    const existing = devices.get(key);
    const incomingCount = typeof message.deviceCount === 'number' && Number.isFinite(message.deviceCount)
      ? message.deviceCount
      : (typeof message.attendeeCount === 'number' && Number.isFinite(message.attendeeCount)
        ? message.attendeeCount
        : undefined);

    const updated: PeerInfo = {
      ...(existing || {
        deviceId: senderDeviceId,
        isCreator: false,
        connected: true,
        lastSeen: Date.now()
      }),
      deviceId: senderDeviceId,
      deviceName: message.deviceName || existing?.deviceName,
      platform: message.platform || existing?.platform,
      userDisplayName: message.userDisplayName || existing?.userDisplayName,
      connected: true,
      lastSeen: Date.now()
    };
    if (incomingCount !== undefined) {
      updated.deviceCount = incomingCount;
      updated.attendeeCount = incomingCount;
    }

    for (const [existingKey, peer] of [...devices.entries()]) {
      if (existingKey !== key && peer.deviceId === senderDeviceId) {
        if (updated.deviceCount === undefined && typeof peer.deviceCount === 'number') {
          updated.deviceCount = peer.deviceCount;
          updated.attendeeCount = peer.attendeeCount;
        }
        devices.delete(existingKey);
      }
    }

    devices.set(key, updated);
    this._connectedDevicesSignal.set(devices);
    this._connectedDevices.next(devices);
  }

  async isEventCreator(eventId: string): Promise<boolean> {
    // In simplified mode, everyone is treated equally
    return false;
  }

  getStoredEventDetails(eventId: string): any {
    try {
      const stored = localStorage.getItem(`event_${eventId}_details`);
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      this.logger.error('WebSocketSignaling', 'Error retrieving event details from local storage', error);
      return null;
    }
  }

  removeStoredEventDetails(eventId: string): void {
    try {
      localStorage.removeItem(`event_${eventId}_details`);
      this.logger.log('WebSocketSignaling', 'Removed event details from local storage', { eventId });
    } catch (error) {
      this.logger.error('WebSocketSignaling', 'Error removing event details from local storage', error);
    }
  }

  storeEventDetails(eventId: string, eventName: string, password: string, countMethod: string): void {
    try {
      const eventDetails = {
        eventId,
        eventName,
        password,
        timestamp: Date.now(),
        countMethod // NEW: Store countMethod
      };
      localStorage.setItem(`event_${eventId}_details`, JSON.stringify(eventDetails));
      this.logger.log('WebSocketSignaling', 'Stored event details to local storage', { eventId, hasPassword: !!password, countMethod }); // Updated log
    } catch (error) {
      this.logger.error('WebSocketSignaling', `Error storing event details for ${eventId}:`, error);
    }
  }

  updateAttendance(data: any): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      // No-op for compatibility, use data channels instead
      this.logger.log('WebSocketSignaling', 'updateAttendance: Use data channels for attendance updates in simplified mode');
    }
  }

  async testServerConnectivity(): Promise<boolean> {
    return await this.connect();
  }

  async testAndroidConnectivity(): Promise<any> {
    const connected = await this.connect();
    return {
      success: connected,
      details: {
        basicConnectivity: connected,
        androidEndpoint: connected,
        socketConnection: connected,
        directWebSocket: connected,
        platform: 'simplified',
        networkInfo: {},
        errors: [],
        tokenStatus: 'not-required',
        transportInfo: { currentTransport: 'websocket' }
      }
    };
  }

  async testJoinApi(eventId: string, password: string): Promise<any> {
    const connected = await this.connect();
    return {
      success: connected,
      details: {
        requestSent: true,
        responseReceived: connected,
        statusCode: connected ? 200 : 500,
        responseData: { success: connected },
        errors: [],
        headers: {},
        timing: { start: Date.now(), end: Date.now(), duration: 0 }
      }
    };
  }

  async robustJoinEvent(eventId: string, password: string): Promise<any> {
    const connected = await this.connect();
    return {
      success: connected,
      eventName: eventId,
      method: 'simplified',
      details: { connected }
    };
  }

  async testAndroidPolling(): Promise<any> {
    return {
      success: false,
      details: {
        handshakeCompleted: false,
        errors: [{ stage: 'polling', error: 'Not supported in simplified mode' }],
        rawResponses: []
      }
    };
  }

  async checkNetworkRestrictions(): Promise<{ restrictions: string[], suggestions: string[] }> {
    return {
      restrictions: [],
      suggestions: ['Use WebSocket connection on port 8081']
    };
  }

  debugForceWebRTCConnections(): void {
    this.logger.log('WebSocketSignaling', 'debugForceWebRTCConnections: Use WebRTC mesh service directly in simplified mode');
  }

  private generateDeviceId(): string {
    return `device-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  private setupWebSocketHandlers(): void {
    if (!this.ws) return;

    this.ws.onopen = () => {
      this.logger.log('WebSocketSignaling', 'Connected to signaling server');
      this._connectionStatusSignal.set(true);
      this._connectionStatus.next(true);
      this.reconnectAttempts = 0;

      // Start keepalive pings to prevent idle disconnects through proxies
      if (this.keepAliveTimer) {
        clearInterval(this.keepAliveTimer);
      }
      this.keepAliveTimer = setInterval(() => {
        try {
          if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            this.ping();
          }
        } catch (err) {
          this.logger.warn('WebSocketSignaling', 'Keepalive ping failed', err);
        }
      }, this.keepAliveIntervalMs);
    };

    this.ws.onmessage = async (event) => {
      try {
        const message: SignalingMessage = JSON.parse(event.data);
        this.logger.log('WebSocketSignaling', 'Received message:', message);

        switch (message.type) {
          case 'welcome':
            this._clientIdSignal.set(message.clientId || null);
            this._totalClientsSignal.set(message.totalClients || 0);
            this._clientId.next(message.clientId || null);
            this._totalClients.next(message.totalClients || 0);
            break;

          case 'event-peers':
            // Handle peers in the same event
            if (message.peers && this.isCurrentEvent(message.eventId)) {
              const peerIds: string[] = [];
              const previous = this._connectedDevices.value;
              const devices = new Map<string, PeerInfo>();

              previous.forEach((peer, key) => {
                if (typeof peer.deviceCount === 'number' || typeof peer.lastKnownDeviceCount === 'number') {
                  devices.set(key, { ...peer, connected: false });
                }
              });

              if (Array.isArray(message.peers)) {
                message.peers.forEach(peer => {
                  if (typeof peer === 'string') {
                    peerIds.push(peer);
                    const existing = devices.get(peer);
                    devices.set(peer, {
                      ...(existing || { deviceId: peer, isCreator: false }),
                      deviceId: existing?.deviceId || peer,
                      isCreator: existing?.isCreator || false,
                      connected: true,
                      lastSeen: Date.now()
                    });
                    return;
                  }

                  peerIds.push(peer.clientId);
                  const incomingCount = typeof peer.deviceCount === 'number' && Number.isFinite(peer.deviceCount)
                    ? peer.deviceCount
                    : (typeof peer.attendeeCount === 'number' && Number.isFinite(peer.attendeeCount)
                      ? peer.attendeeCount
                      : undefined);
                  const existing = devices.get(peer.clientId)
                    || [...devices.values()].find(item => item.deviceId === (peer.deviceId || peer.clientId));
                  const updated: PeerInfo = {
                    ...(existing || {
                      deviceId: peer.deviceId || peer.clientId,
                      isCreator: false,
                      connected: true,
                      lastSeen: Date.now()
                    }),
                    deviceId: peer.deviceId || existing?.deviceId || peer.clientId,
                    deviceName: peer.deviceName || existing?.deviceName,
                    platform: peer.platform || existing?.platform,
                    userDisplayName: peer.userDisplayName || existing?.userDisplayName,
                    isCreator: existing?.isCreator || false,
                    connected: true,
                    lastSeen: Date.now()
                  };
                  if (incomingCount !== undefined) {
                    updated.deviceCount = incomingCount;
                    updated.attendeeCount = incomingCount;
                  }
                  for (const [mapKey, item] of [...devices.entries()]) {
                    if (mapKey !== peer.clientId && item.deviceId === updated.deviceId) {
                      devices.delete(mapKey);
                    }
                  }
                  devices.set(peer.clientId, updated);
                });
              }

              this._peers.next(peerIds);
              this._connectedDevicesSignal.set(devices);
              this._connectedDevices.next(devices);
              this.logger.log('WebSocketSignaling', `Received ${peerIds.length} event peers for event ${message.eventId}:`, peerIds);
            }
            break;

          case 'peer-joined':
            // Only handle peers from the same event
            if (this.isCurrentEvent(message.eventId) && message.clientId) {
              this._totalClients.next(message.totalClients || 0);

              const currentPeers = this._peers.value;
              if (!currentPeers.includes(message.clientId)) {
                this._peers.next([...currentPeers, message.clientId]);
              }

              this.upsertPeerAttendance({
                clientId: message.clientId,
                deviceId: message.deviceId || message.clientId,
                deviceName: message.deviceName,
                platform: message.platform,
                userDisplayName: message.userDisplayName,
                deviceCount: message.deviceCount,
                attendeeCount: message.attendeeCount
              });
              this.logger.log('WebSocketSignaling', `Peer ${message.userDisplayName || message.deviceName || message.clientId} joined event ${message.eventId}:`, {
                deviceName: message.deviceName,
                platform: message.platform,
                userDisplayName: message.userDisplayName,
                deviceCount: message.deviceCount
              });
            }
            break;

          case 'peer-left':
            // Only handle peers from the same event
            if (this.isCurrentEvent(message.eventId) && message.clientId) {
              this._totalClients.next(message.totalClients || 0);

              const currentPeers = this._peers.value;
              this._peers.next(currentPeers.filter(id => id !== message.clientId));

              // Update connected devices - mark as disconnected instead of removing
              const devices = this._connectedDevices.value;
              const peerToUpdate = devices.get(message.clientId);
              if (peerToUpdate) {
                const updatedPeer: PeerInfo = {
                  ...peerToUpdate,
                  connected: false,
                  disconnectedAt: Date.now(),
                  lastKnownAttendeeCount: peerToUpdate.attendeeCount,
                  lastKnownDeviceCount: peerToUpdate.deviceCount
                };
                const newDevices = new Map(devices);
                newDevices.set(message.clientId, updatedPeer);
                this._connectedDevicesSignal.set(newDevices);
                this._connectedDevices.next(newDevices);

                // Persist disconnected device to storage
                if (this.currentEventId) {
                  await this.persistDisconnectedDevices(this.currentEventId);
                }

                this.logger.log('WebSocketSignaling', `Peer ${message.clientId} left event ${message.eventId} with counts:`, {
                  attendeeCount: updatedPeer.lastKnownAttendeeCount,
                  deviceCount: updatedPeer.lastKnownDeviceCount
                });
              }
            }
            break;

          case 'attendance-update':
            if (!message.eventId || this.isCurrentEvent(message.eventId)) {
              this.upsertPeerAttendance({
                clientId: message.clientId || message.fromId,
                deviceId: message.deviceId,
                deviceName: message.deviceName,
                platform: message.platform,
                userDisplayName: message.userDisplayName,
                deviceCount: message.deviceCount,
                attendeeCount: message.attendeeCount
              });
            }
            break;

          case 'existing-peers':
            // Legacy support - ignore if we're using event-based connections
            if (!this.currentEventId && message.peers && Array.isArray(message.peers)) {
              // For legacy support, assume all peers are strings
              const peerStrings = message.peers.filter(p => typeof p === 'string') as string[];
              this._peers.next(peerStrings);

              // Update connected devices
              const devices = new Map<string, PeerInfo>();
              peerStrings.forEach(peerId => {
                devices.set(peerId, {
                  deviceId: peerId,
                  isCreator: false,
                  connected: true,
                  lastSeen: Date.now()
                });
              });
              this._connectedDevicesSignal.set(devices);
              this._connectedDevices.next(devices);
            }
            break;
        }

        this._messages.next(message);
      } catch (error) {
        this.logger.error('WebSocketSignaling', 'Error parsing message:', error);
      }
    };

    this.ws.onclose = (event) => {
      this.logger.log('WebSocketSignaling', `WebSocket connection closed: ${event.code} - ${event.reason || 'No reason provided'}`);
      this._connectionStatusSignal.set(false);
      this._connectionStatus.next(false);

      // Stop keepalive timer
      if (this.keepAliveTimer) {
        clearInterval(this.keepAliveTimer);
        this.keepAliveTimer = null;
      }

      // Enhanced error reporting for older Android devices
      if (event.code !== 1000) { // Not a normal closure
        this.logger.warn('WebSocketSignaling', 'Abnormal WebSocket closure detected');
        this.logger.log('WebSocketSignaling', 'This may indicate network connectivity issues on older Android devices');
        this.logger.log('WebSocketSignaling', 'Possible causes: Corporate firewall, proxy settings, or SSL/TLS compatibility');

        this.scheduleReconnect();
      }
    };

    this.ws.onerror = (error) => {
      this.logger.error('WebSocketSignaling', 'WebSocket error:', error);
      this.logger.log('WebSocketSignaling', 'WebSocket error details for debugging on older Android devices:');
      this.logger.log('WebSocketSignaling', '- Check if device has internet connectivity');
      this.logger.log('WebSocketSignaling', '- Verify WebSocket server is accessible');
      this.logger.log('WebSocketSignaling', '- Consider network restrictions or proxy settings');
      this._connectionStatusSignal.set(false);
      this._connectionStatus.next(false);
    };
  }

  private scheduleReconnect(serverUrl?: string): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      this.logger.error('WebSocketSignaling', 'Max reconnection attempts reached');
      return;
    }

    this.reconnectAttempts++;
    this.logger.log('WebSocketSignaling', `Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts}) in ${this.reconnectInterval}ms`);

    setTimeout(() => {
      // Always attempt to reconnect using the provided URL or the last known server URL
      const urlToUse = serverUrl || this.serverUrl;
      this.connect(urlToUse);
    }, this.reconnectInterval);
  }

  get clientId(): string | null {
    return this._clientId.value;
  }

  get isConnected(): boolean {
    return this._connectionStatus.value;
  }

  get totalClients(): number {
    return this._totalClients.value;
  }

  get peers(): string[] {
    return this._peers.value;
  }

  /**
   * Get the storage key for disconnected devices for a specific event
   */
  private getDisconnectedDevicesKey(eventId: string): string {
    return `disconnected_devices_${eventId}`;
  }

  /**
   * Persist disconnected devices to storage
   */
  private async persistDisconnectedDevices(eventId: string): Promise<void> {
    if (!this.currentEventId || this.currentEventId !== eventId) return;

    try {
      const devices = this._connectedDevicesSignal();
      const disconnectedDevices: PeerInfo[] = [];

      devices.forEach(device => {
        if (!device.connected && device.disconnectedAt) {
          disconnectedDevices.push(device);
        }
      });

      const key = this.getDisconnectedDevicesKey(eventId);
      await this.storage.set(key, JSON.stringify(disconnectedDevices));

      this.logger.log('WebSocketSignaling', `Persisted ${disconnectedDevices.length} disconnected devices for event ${eventId}`);
    } catch (error) {
      this.logger.error('WebSocketSignaling', 'Failed to persist disconnected devices:', error);
    }
  }

  /**
   * Load disconnected devices from storage
   */
  private async loadDisconnectedDevices(eventId: string): Promise<PeerInfo[]> {
    try {
      const key = this.getDisconnectedDevicesKey(eventId);
      const stored = await this.storage.get(key);

      if (!stored) return [];

      const disconnectedDevices: PeerInfo[] = JSON.parse(stored);

      // Filter out devices older than 24 hours
      const maxAge = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
      const now = Date.now();

      const recentDevices = disconnectedDevices.filter(device =>
        device.disconnectedAt && (now - device.disconnectedAt) < maxAge
      );

      this.logger.log('WebSocketSignaling', `Loaded ${recentDevices.length} recent disconnected devices for event ${eventId}`);
      return recentDevices;
    } catch (error) {
      this.logger.error('WebSocketSignaling', 'Failed to load disconnected devices:', error);
      return [];
    }
  }

  /**
   * Merge disconnected devices with current connected devices
   */
  private async mergeDisconnectedDevices(eventId: string): Promise<void> {
    if (!this.currentEventId || this.currentEventId !== eventId) return;

    try {
      const disconnectedDevices = await this.loadDisconnectedDevices(eventId);

      if (disconnectedDevices.length === 0) return;

      const currentDevices = new Map(this._connectedDevicesSignal());

      // Add disconnected devices that aren't already in the current devices map
      disconnectedDevices.forEach(disconnectedDevice => {
        if (!currentDevices.has(disconnectedDevice.deviceId)) {
          currentDevices.set(disconnectedDevice.deviceId, {
            ...disconnectedDevice,
            connected: false
          });
        }
      });

      this._connectedDevicesSignal.set(currentDevices);
      this._connectedDevices.next(currentDevices);

      this.logger.log('WebSocketSignaling', `Merged ${disconnectedDevices.length} disconnected devices into device list`);
    } catch (error) {
      this.logger.error('WebSocketSignaling', 'Failed to merge disconnected devices:', error);
    }
  }

  /**
   * Clean up old disconnected devices from storage
   */
  public async cleanupOldDisconnectedDevices(eventId: string, maxAgeHours: number = 24): Promise<void> {
    try {
      const disconnectedDevices = await this.loadDisconnectedDevices(eventId);
      const maxAge = maxAgeHours * 60 * 60 * 1000;
      const now = Date.now();

      const recentDevices = disconnectedDevices.filter(device =>
        device.disconnectedAt && (now - device.disconnectedAt) < maxAge
      );

      const key = this.getDisconnectedDevicesKey(eventId);
      await this.storage.set(key, JSON.stringify(recentDevices));

      const removedCount = disconnectedDevices.length - recentDevices.length;
      if (removedCount > 0) {
        this.logger.log('WebSocketSignaling', `Cleaned up ${removedCount} old disconnected devices for event ${eventId}`);
      }
    } catch (error) {
      this.logger.error('WebSocketSignaling', 'Failed to cleanup old disconnected devices:', error);
    }
  }
} 